
import { defineComponent } from 'vue';
import ConfirmCancelAutopayDialog from '@/components/common/Dialogs/ConfirmCancelAutopayDialog.vue';
import ConfirmAutopayDialog from '@/components/common/Dialogs/ConfirmAutopayDialog.vue';
import CustomButton from '~/components/Buttons/CustomButton.vue';
import LoanDataMixin from '@/mixins/LoanDataMixin';
import PaymentOptionSelect from '@/components/common/Inputs/PaymentOptionSelect.vue';
import AutopayTermsDialog from '@/components/common/Dialogs/AutopayTermsDialog.vue';
import { PaymentProviderBankDetails } from '@/interfaces/bank-account';
import { PaymentProviderCardDetails } from '@/interfaces/moov';
import { AutopayDetails } from '@/interfaces/loan';
import { PaymentOptions, selectedPaymentOption } from '@/interfaces/payment-options';
import { subscribe, unsubscribe, events } from '@/events/eventBus';
import AutopayActive from '@/components/Payments/AutopayActive.vue';

export default defineComponent({
  name: 'AutopayComponent',

  components: {
    CustomButton,
    PaymentOptionSelect,
    AutopayActive
  },

  mixins: [LoanDataMixin],

  data() {
    const selectedPaymentOpt: selectedPaymentOption | null = {
      id: '',
      type: '',
      text: '',
      lastFour: '',
      paymentProviderDetails: '',
    };
    return {
      acceptTerms: false,
      selectedPaymentOption: selectedPaymentOpt,
      loading: true,
    };
  },

  computed: {
    loanNumber() {
      return this.loanData.loan_number;
    },

    bankSelected() {
      return this.selectedPaymentOption?.type === PaymentOptions.BANK;
    },

    cardSelected() {
      return this.selectedPaymentOption?.type === PaymentOptions.CARD;
    },

    autopayDetails(): AutopayDetails | null {
      return this.$store.getters['Loan/getAutopayDetails'];
    },

    hasActiveAutopay(): boolean {
      return !!this.autopayDetails?.payment_provider_option_id;
    },

    bankDetails(): PaymentProviderBankDetails {
      return this.$store.getters['PaymentOptions/getBankAccount'];
    },

    cardDetails(): PaymentProviderCardDetails {
      return this.$store.getters['PaymentOptions/getCard'];
    },

    hasMultiplePaymentMethods() {
      if (this.bankDetails && this.cardDetails) {
        return Object.keys(this.bankDetails).length > 0 && Object.keys(this.cardDetails).length > 0;
      }
      return false;
    },
  },

  destroyed() {
    unsubscribe(events.PAYMENT_OPTIONS_UPDATE, this.load);
  },

  async mounted() {
    await this.load();
    subscribe(events.PAYMENT_OPTIONS_UPDATE, this.load);
  },

  methods: {
    showConfirmationDialog() {
      this.$dialog({
        component: ConfirmAutopayDialog,
        props: {
          lastFour: this.selectedPaymentOption?.lastFour,
          type: this.selectedPaymentOption?.type,
          paymentProviderDetails: this.selectedPaymentOption?.paymentProviderDetails,
          paymentProviderOptionId: this.selectedPaymentOption?.id,
        }
      });
    },

    showCancelConfirmationDialog() {
      this.$dialog({
        component: ConfirmCancelAutopayDialog
      });
      this.acceptTerms = false;
    },

    showTerms() {
      this.$dialog({
        component: AutopayTermsDialog,
        props: {
          paymentMethodType: this.selectedPaymentOption?.type,
        }
      });
    },

    async load() {
      this.loading = true;

      await this.$store.dispatch('Loan/getAutopayInfo');
      this.acceptTerms = false;
      if (!this.hasMultiplePaymentMethods) {
        if (this.bankDetails) {
          this.selectedPaymentOption = {
            id: this.bankDetails.id as string,
            type: 'bank',
            text: `Bank account ending in ${this.bankDetails.bank_account_last_4}`,
            lastFour: this.bankDetails.bank_account_last_4,
            paymentProviderDetails: this.bankDetails.payment_provider_details_id,
          };
        } else {
          this.selectedPaymentOption = {
            id: this.cardDetails.id,
            type: 'card',
            text: `Debit card ending in ${this.cardDetails.card_last_4}`,
            lastFour: this.cardDetails.card_last_4,
            paymentProviderDetails: this.cardDetails.payment_provider_details_id,
          };
        }
      }

      this.loading = false;
    },
  },
});
